// Activity log actor for leads created or assigned without a user (no "System" prefix).
// Integration rule: "<form name> - <channel type>", e.g. "AndaBreeze - Website".
// See docs/ACTIVITY-LOG-SYSTEM-ACTOR.md.

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

/**
 * Channel type label for a form, based on the form's origin
 * (set by the backend when the channel or form is created).
 * Returns undefined when the origin is unknown, so the label falls back to the name only.
 */
export const getFormChannelTypeLabel = (form, t) => {
  if (!form) {
    return undefined;
  }
  switch (form.origin) {
    case 'website':
      return capitalize(t('entity.website', 'website'));
    case 'mhub_lead':
    case 'mhub_customer':
    case 'mhub':
      return t('component.label.mhub_form', 'MHub form');
    case 'facebook':
      return t('component.label.facebook_form', 'Facebook form');
    case 'tiktok':
      return t('component.label.tiktok_form', 'TikTok form');
    case 'iproperty':
    case 'property_guru':
      return t('component.label.listing_portal', 'Listing portal');
    default:
      // Older form lists may not carry origin; Facebook forms are still recognisable
      return form.fb_form_id ? t('component.label.facebook_form', 'Facebook form') : undefined;
  }
};

const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const formActorName = (name, form, t) => {
  const channelType = getFormChannelTypeLabel(form, t);
  return channelType ? `${name} - ${channelType}` : name;
};

// Label for a form: current name and channel type, or the saved name marked deleted.
const getFormLabel = (formID, formName, formList, t) => {
  const form = (formList || []).find((f) => f.id === formID);
  if (form) {
    return formActorName(form.name || formID, form, t);
  }
  return `${formName || t('component.text.n/a', 'n/a')} (${t('component.label.deleted', 'Deleted')})`;
};

/**
 * Label for an activity that has no user actor but was assigned by a user group or a form.
 * Entries without either fall back to the form the lead came from (sourceForm).
 * Returns null when the activity is not such an assignment.
 */
export const getSystemActorLabel = (activity, {
  t, userGroupList, formList, formLoading, sourceForm,
}) => {
  const values = activity && activity.new_values;
  if (!values || !(has(values, 'user_group_id') || has(values, 'form_id'))) {
    return null;
  }

  const na = t('component.text.n/a', 'n/a');
  const deleted = t('component.label.deleted', 'Deleted');
  const loading = `${t('component.label.loading', 'Loading')}...`;

  if (values.form_id && has(values, 'form_name')) {
    return formLoading ? loading : getFormLabel(values.form_id, values.form_name, formList, t);
  }

  if (values.user_group_id) {
    // Get updated user group name if have
    const group = (userGroupList || []).find((g) => g.id === values.user_group_id);
    if (group && group.assignment_name) {
      return group.assignment_name;
    }
    return `${values.user_group_assignment_name || na} (${deleted})`;
  }

  // Assignee fields from an external factor (e.g. import) with no group or form
  if (sourceForm && sourceForm.form_id) {
    if (formLoading) {
      return loading;
    }
    return getFormLabel(sourceForm.form_id, sourceForm.form_name, formList, t);
  }
  return na;
};
